import { useEffect, useState } from "react";

import { state } from "../state/workspaceStore.js";

import { save } from "../utils/domain/model.js";

import { notFound } from "./NotFoundPage.jsx";

import { head } from "../components/common/PageHeader.jsx";

import { size, esc, money } from "../utils/format.js";

import { btn } from "../components/forms/FormControls.jsx";

import { image } from "../components/products/ProductImage.jsx";

import { badge } from "../components/common/StatusBadge.jsx";

import { nodes } from "../components/common/reactHelpers.jsx";

import { historyTable } from "../components/products/StockHistoryTable.jsx";

import { detailRows } from "../components/common/DetailRows.jsx";

import { tableViews } from "../components/tables/RecordTable.jsx";

/* =========================================================

   PRODUCT API

\========================================================= */

const PRODUCT_API = "https://api.praachinorganics.com/api/products";

/* =========================================================

   PRODUCT STATUS

\========================================================= */

function statusFromApi(value) {
  if (value === "Draft") {
    return "Draft";
  }

  if (value === "Active") {
    return "Active";
  }

  if (value === "ComingSoon" || value === "Coming Soon") {
    return "Coming Soon";
  }

  if (value === "OutOfStock" || value === "Out of Stock") {
    return "Out of Stock";
  }

  return (
    {
      1: "Draft",

      2: "Active",

      3: "Coming Soon",

      4: "Out of Stock",
    }[Number(value)] || "Draft"
  );
}

/* =========================================================

   QUANTITY UNIT

\========================================================= */

function unitFromApi(value) {
  if (value === "Gram") {
    return "g";
  }

  if (value === "Kilogram") {
    return "kg";
  }

  if (value === "Millilitre") {
    return "ml";
  }

  if (value === "Litre") {
    return "l";
  }

  return (
    {
      1: "g",

      2: "kg",

      3: "ml",

      4: "l",
    }[Number(value)] || "g"
  );
}

/* =========================================================

   API ERROR

\========================================================= */

function apiError(
  data,

  status,
) {
  if (data?.errors) {
    return Object.values(data.errors)

      .flat()

      .join("\n");
  }

  return (
    data?.message ||
    data?.title ||
    (typeof data === "string" ? data : "") ||
    `API request failed with status ${status}.`
  );
}

/* =========================================================

   API REQUEST

\========================================================= */

async function request(url) {
  console.log(
    "API CALL: GET",

    url,
  );

  try {
    const response = await fetch(
      url,

      {
        method: "GET",

        headers: {
          Accept: "application/json",
        },
      },
    );

    const contentType = response.headers.get("content-type") || "";

    let data = null;

    try {
      data = contentType.includes("application/json")
        ? await response.json()
        : await response.text();
    } catch (readError) {
      console.error(
        "API RESPONSE READ ERROR:",

        readError,
      );
    }

    console.log(
      "API STATUS:",

      response.status,

      response.statusText,
    );

    console.log(
      "API RESPONSE:",

      data,
    );

    if (!response.ok) {
      const error = new Error(
        apiError(
          data,

          response.status,
        ),
      );

      error.status = response.status;

      throw error;
    }

    return data;
  } catch (error) {
    console.error(
      "PRODUCT DETAILS API FAILED:",

      error,
    );

    console.error(
      "REQUEST URL:",

      url,
    );

    if (error instanceof TypeError) {
      throw new Error(
        "Unable to connect to the backend API. Please verify that the backend is running, the HTTPS certificate is trusted, and CORS is enabled.",
      );
    }

    throw error;
  }
}

/* =========================================================

   PRODUCT DETAILS RESPONSE -> UI MODEL

\========================================================= */

function mapProductDetails(item) {
  const variant = item?.variant ?? item?.Variant ?? null;

  const productImage = item?.image ?? item?.Image ?? null;

  const imageUrl = productImage?.imageUrl ?? productImage?.ImageUrl ?? null;

  /*

    ProductDetailsResponse contains CollectionName,

    but currently does not contain CollectionId.



    For details UI we therefore use collectionName

    directly from the API response.

  */

  const collectionName = item?.collectionName ?? item?.CollectionName ?? "";

  const dimensions = variant
    ? [
      variant?.lengthCm ?? variant?.LengthCm,

      variant?.widthCm ?? variant?.WidthCm,

      variant?.heightCm ?? variant?.HeightCm,
    ]

      .filter(
        (value) => value !== null && value !== undefined && value !== "",
      )

      .join(" × ")
    : "";

  return {
    id: String(item?.id ?? item?.Id ?? ""),

    name: item?.name ?? item?.Name ?? "",

    /*

      We do not receive CollectionId from this DTO.

    */

    collection: "",

    collectionName,

    description: item?.description ?? item?.Description ?? "",

    status: statusFromApi(item?.status ?? item?.Status),

    /*

      Existing UI uses launch.

      Active product is treated as launched.

    */

    launch:
      (item?.statusName ?? item?.StatusName) === "Active" ||
      Number(item?.status ?? item?.Status) === 2,

    infoMode:
      item?.informationProvenance ?? item?.InformationProvenance ?? "reference",

    taxRate: item?.gstRate ?? item?.GstRate ?? null,

    ingredients: item?.ingredients ?? item?.Ingredients ?? "",

    nutrition: item?.nutrition ?? item?.Nutrition ?? "",

    allergens: item?.allergenDeclaration ?? item?.AllergenDeclaration ?? "",

    shelfLife: item?.shelfLife ?? item?.ShelfLife ?? "",

    storage: item?.storageInstructions ?? item?.StorageInstructions ?? "",

    manufacturer: [
      item?.manufacturerName ?? item?.ManufacturerName,

      item?.manufacturerAddress ?? item?.ManufacturerAddress,
    ]

      .filter(Boolean)

      .join("\n"),

    marketer: [
      item?.marketerName ?? item?.MarketerName,

      item?.marketerAddress ?? item?.MarketerAddress,
    ]

      .filter(Boolean)

      .join("\n"),

    fssai: item?.fssaiLicenseDetails ?? item?.FssaiLicenseDetails ?? "",

    complianceVerified: Boolean(
      item?.labelComplianceReviewed ?? item?.LabelComplianceReviewed,
    ),

    photos: imageUrl ? [imageUrl] : [],

    /*

      Backend currently returns one primary Variant.

      Existing UI expects p.skus array.

    */

    skus: variant
      ? [
        {
          id: String(variant?.id ?? variant?.Id ?? ""),

          code: variant?.skuCode ?? variant?.SkuCode ?? "",

          variant: variant?.variantName ?? variant?.VariantName ?? "",

          qty: variant?.netQuantity ?? variant?.NetQuantity ?? "",

          unit: unitFromApi(variant?.unit ?? variant?.Unit),

          mrp: variant?.mrp ?? variant?.Mrp ?? 0,

          discount:
            variant?.discountPercentage ?? variant?.DiscountPercentage ?? 0,

          price: variant?.sellingPrice ?? variant?.SellingPrice ?? 0,

          stock: variant?.stock ?? variant?.Stock ?? 0,

          threshold:
            variant?.lowStockThreshold ?? variant?.LowStockThreshold ?? 0,

          weight:
            variant?.packedWeightGrams ?? variant?.PackedWeightGrams ?? "",

          dimensions,
        },
      ]
      : [],

    variant: variant?.variantName ?? variant?.VariantName ?? "",

    createdAt: item?.createdAt ?? item?.CreatedAt ?? null,

    updatedAt: item?.updatedAt ?? item?.UpdatedAt ?? null,

    archived: false,
  };
}

/* =========================================================

   SYNC PRODUCT INTO EXISTING LOCAL UI STATE

\========================================================= */

function syncProductToState(mappedProduct) {
  if (!mappedProduct?.id) {
    return;
  }

  if (!Array.isArray(state.db.products)) {
    state.db.products = [];
  }

  const index = state.db.products.findIndex(
    (product) => String(product.id) === String(mappedProduct.id),
  );

  if (index < 0) {
    state.db.products.push(mappedProduct);
  } else {
    /*

      Backend values take priority.

      Existing local-only fields can remain available.

    */

    state.db.products[index] = {
      ...state.db.products[index],

      ...mappedProduct,
    };
  }

  try {
    save(
      localStorage,

      state.db,
    );
  } catch (error) {
    console.error(
      "LOCAL PRODUCT SYNC FAILED:",

      error,
    );
  }
}

/* =========================================================

   PRODUCT DETAILS UI

\========================================================= */

export function productDetail(p) {
  if (!p) {
    return notFound();
  }

  const collectionLabel = p.collectionName || "Unassigned";

  return (
    <>
      {head(
        "PRODUCT / " + collectionLabel,

        p.name,

        p.skus?.length
          ? p.skus

            .map(size)

            .join(" · ")
          : "",

        <>
          {btn(
            "Edit Product",

            `data-nav="product-edit" data-id="${p.id}"`,
          )}
        </>,
      )}

      <div className="grid-detail">
        <div className="stack">
          <section className="card">
            <div className="hero-product">
              {image(
                p,

                "large-img",
              )}

              <div>
                {badge(p.archived ? "Archived" : p.status)}

                <p>{esc(p.description)}</p>
              </div>
            </div>

            {p.photos?.length > 1 ? (
              <div className="photo-strip">
                {nodes(
                  p.photos

                    .slice(1)

                    .map(
                      (
                        src,

                        index,
                      ) => (
                        <img
                          key={`${src}-${index}`}
                          src={esc(src)}
                          alt={esc(p.name) + " additional packaging view"}
                        />
                      ),
                    ),
                )}
              </div>
            ) : (
              ""
            )}

            <h2>SKU variants & inventory</h2>

            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>SKU / variant</th>

                    <th>Size</th>

                    <th>Price / MRP</th>

                    <th>Discount %</th>

                    <th>Stock</th>

                    <th>Threshold</th>
                  </tr>
                </thead>

                <tbody>
                  {p.skus?.length ? (
                    nodes(
                      p.skus.map((sku) => (
                        <tr key={sku.id || sku.code}>
                          <td>
                            <b>{esc(sku.code)}</b>

                            <small>{esc(sku.variant || p.variant)}</small>
                          </td>

                          <td>{size(sku)}</td>

                          <td>
                            {money(sku.price)}

                            {" / "}

                            {money(sku.mrp)}
                          </td>

                          <td>
                            {Number(sku.discount ?? 0)
                              .toFixed(4)

                              .replace(
                                /0+$/,

                                "",
                              )

                              .replace(
                                /\.$/,

                                "",
                              )}

                            {"%"}
                          </td>

                          <td>{sku.stock}</td>

                          <td>{sku.threshold}</td>
                        </tr>
                      )),
                    )
                  ) : (
                    <tr>
                      <td colSpan="6">No variant information available.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>

        <div className="stack">
          <section className="card">
            <h2>Product identity</h2>

            {detailRows([
              ["Name", p.name],

              ["Collection", collectionLabel],

              ["Primary flavour", p.variant],

              ["Status", p.status],

              ["Launch", p.launch ? "Yes" : "No"],

              ["Information provenance", p.infoMode],

              [
                "Label/compliance reviewed",

                p.complianceVerified ? "Yes" : "No",
              ],
            ])}

            <h2>Product information</h2>

            <p className="hint">
              {p.infoMode === "verified" && p.complianceVerified
                ? "Label details marked verified in this workspace."
                : "Reference content · confirm against the approved label before public use."}
            </p>

            {nodes(
              [
                "ingredients",

                "nutrition",

                "allergens",

                "shelfLife",

                "storage",

                "manufacturer",

                "marketer",
              ].map((key) => (
                <div className="detail-row" key={key}>
                  <span>
                    {key.replace(
                      /([A-Z])/g,

                      " $1",
                    )}
                  </span>

                  <b>{esc(p[key] || "Label review required")}</b>
                </div>
              )),
            )}

            <div className="detail-row">
              <span>FSSAI</span>

              <b>{esc(p.fssai || "Licence verification required")}</b>
            </div>

            <div className="detail-row">
              <span>GST rate</span>

              <b>
                {p.taxRate == null
                  ? "Tax classification review required"
                  : esc(p.taxRate) + "%"}
              </b>
            </div>

            <div className="divider"></div>

            <h2>Shipping pack</h2>

            {p.skus?.length ? (
              nodes(
                p.skus.map((sku) => (
                  <p className="hint" key={sku.id || sku.code}>
                    <b>{esc(sku.code)}</b>

                    {" · "}

                    {sku.weight || "—"}

                    {" g · "}

                    {esc(sku.dimensions || "—")}

                    {" cm"}
                  </p>
                )),
              )
            ) : (
              <p className="hint">Shipping information is not available.</p>
            )}
          </section>
        </div>
      </div>
    </>
  );
}

/* =========================================================

   PRODUCT DETAILS PAGE

\========================================================= */

export default function ProductDetailsPage({ recordId } = {}) {
  const [productData, setProductData] = useState(null);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState("");

  const [productNotFound, setProductNotFound] = useState(false);

  /* =======================================================

     GET PRODUCT BY ID



     Example:

     recordId = 1



     GET

     https://api.praachinorganics.com/api/products/1

  ======================================================= */

  useEffect(() => {
    let active = true;

    async function loadProduct() {
      setLoading(true);

      setError("");

      setProductNotFound(false);

      try {
        /* ===============================================

           VALIDATE ID

        =============================================== */

        const productId = Number(recordId);

        if (!Number.isInteger(productId) || productId <= 0) {
          throw new Error("A valid product ID is required.");
        }

        const url = `${PRODUCT_API}/${productId}`;

        console.log(
          "GET PRODUCT DETAILS:",

          url,
        );

        /* ===============================================

           CALL GET BY ID

        =============================================== */

        const response = await request(url);

        if (!active) {
          return;
        }

        console.log(
          "PRODUCT DETAILS RESPONSE:",

          response,
        );

        /* ===============================================

           MAP API -> EXISTING UI MODEL

        =============================================== */

        const mappedProduct = mapProductDetails(response);

        console.log(
          "MAPPED PRODUCT DETAILS:",

          mappedProduct,
        );

        if (!mappedProduct.id) {
          throw new Error("The Product API returned an invalid product.");
        }

        /* ===============================================

           SYNC EXISTING WORKSPACE STATE



           This helps existing Edit Product navigation.

        =============================================== */

        syncProductToState(mappedProduct);

        /* ===============================================

           RENDER

        =============================================== */

        setProductData(mappedProduct);
      } catch (loadError) {
        console.error(
          "PRODUCT DETAILS LOAD FAILED:",

          loadError,
        );

        if (!active) {
          return;
        }

        if (loadError?.status === 404) {
          setProductNotFound(true);

          setProductData(null);

          return;
        }

        setError(loadError?.message || "Unable to load product details.");

        setProductData(null);
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    loadProduct();

    return () => {
      active = false;
    };
  }, [recordId]);

  /* =======================================================

     LOADING

  ======================================================= */

  if (loading) {
    return tableViews(
      <>
        {head(
          "PRODUCT",

          "Loading product...",

          "",

          <></>,
        )}

        <section className="card">
          <p className="hint">Loading product details...</p>
        </section>
      </>,
    );
  }

  /* =======================================================

     404

  ======================================================= */

  if (productNotFound) {
    return tableViews(notFound());
  }

  /* =======================================================

     ERROR

  ======================================================= */

  if (error) {
    return tableViews(
      <>
        {head(
          "PRODUCT",

          "Unable to load product",

          "",

          <></>,
        )}

        <section className="card">
          <p className="error" role="alert">
            {error}
          </p>
        </section>
      </>,
    );
  }

  /* =======================================================

     DETAILS

  ======================================================= */

  return tableViews(productDetail(productData));
}
